import { Loader2, UserCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { api } from '@/lib/api';
import { useAuth } from './AuthContext';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileDialog({ open, onOpenChange }: Props) {
  const { user, setUser } = useAuth();
  const [displayName, setDisplayName] = useState(user?.display_name ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDisplayName(user?.display_name ?? '');
  }, [open, user?.display_name]);

  if (!user) return null;

  const dirty = displayName.trim() !== (user.display_name ?? '') && displayName.trim().length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { user: updated } = await api.updateProfile({ display_name: displayName.trim() });
      setUser({ ...user, display_name: updated.display_name });
      toast.success('Profile updated');
      onOpenChange(false);
    } catch (err) {
      toast.error('Update failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCircle2 className="h-4 w-4" /> Your profile
          </DialogTitle>
          <DialogDescription>
            Only display name is editable. Email and role are managed by an admin.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="prof-email">Email</Label>
            <Input id="prof-email" value={user.email} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="prof-role">Role</Label>
            <Input id="prof-role" value={user.role} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="prof-name">Display name</Label>
            <Input
              id="prof-name"
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dirty && !saving) void save();
              }}
              maxLength={80}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button onClick={save} disabled={!dirty || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
